/**
 * Suppliers.tsx — V4-0-5 / ADR-0026.
 *
 * Workspace-scoped supplier portfolio. Fetches from /api/w/:slug/suppliers
 * (workspace scope, not org scope). Write actions: add supplier,
 * request disclosure, manage grants, set risk bar.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, PageHeader, Spinner } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { req } from '../lib/api';

interface PortfolioEntry {
  portfolio_entry_id: string;
  supplier_display_name: string;
  risk_tier: string | null;
  vertical: string | null;
  disclosure_version_id: string | null;
  disclosure_source: string | null;
  created_at: string;
}

interface SearchResult {
  supplier_display_name: string;
  domain: string | null;
  disclosure: { version_id: string; published_at: string } | null;
  already_tracked: boolean;
  portfolio_entry_id: string | null;
  request_open: boolean;
  can_request_disclosure: boolean;
}

async function listSuppliers(slug: string): Promise<{ entries: PortfolioEntry[] }> {
  return req<{ entries: PortfolioEntry[] }>(`/api/w/${encodeURIComponent(slug)}/suppliers`);
}

async function searchRegister(slug: string, domain: string): Promise<{ results: SearchResult[] }> {
  return req<{ results: SearchResult[] }>(
    `/api/w/${encodeURIComponent(slug)}/suppliers/search?domain=${encodeURIComponent(domain)}`
  );
}

export function Suppliers() {
  const { workspace } = useWorkspace();
  const prefix = `/w/${workspace.slug}/app`;
  const suppliers = useWorkspaceQuery(
    (s) => queryKeys.suppliers(s),
    (s) => listSuppliers(s),
  );
  const [adding, setAdding] = useState(false);

  if (suppliers.loading) return <Spinner label="Loading suppliers" />;
  if (suppliers.error) return <Alert variant="error">{suppliers.error}</Alert>;

  const list = suppliers.data?.entries ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <PageHeader
          title="Third-Party AI"
          subtitle={`${list.length} supplier${list.length !== 1 ? 's' : ''} tracked`}
        />
        <Button onClick={() => setAdding(true)}>Add supplier</Button>
      </div>

      <SupplierSearch slug={workspace.slug} />

      {list.length > 0 ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {list.map((entry) => (
            <Link
              key={entry.portfolio_entry_id}
              to={`${prefix}/suppliers/${entry.portfolio_entry_id}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Card>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                }}>
                  <StatusDot hasDisclosure={!!entry.disclosure_version_id} />
                  <span style={{
                    flex: 1,
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-2xs)',
                    fontWeight: 500,
                    color: 'var(--text)',
                  }}>
                    {entry.supplier_display_name}
                  </span>
                  {entry.risk_tier && (
                    <span style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-3xs)',
                      color: 'var(--text-dim)',
                      textTransform: 'capitalize',
                    }}>
                      {entry.risk_tier}
                    </span>
                  )}
                  {entry.vertical && (
                    <span style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-3xs)',
                      color: 'var(--text-dim)',
                    }}>
                      {entry.vertical}
                    </span>
                  )}
                  <span style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-3xs)',
                    color: entry.disclosure_version_id ? 'var(--success-fg)' : 'var(--text-dim)',
                  }}>
                    {entry.disclosure_version_id ? 'Disclosed' : 'Pending'}
                  </span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No suppliers tracked yet"
          description="Add your first supplier to start building your third-party AI register. Import from CSV or add manually."
        />
      )}

      {adding && (
        <AddSupplierDialog
          slug={workspace.slug}
          onClose={() => { setAdding(false); window.location.reload(); }}
        />
      )}
    </div>
  );
}

/**
 * SupplierSearch — V85-5-7 (gap A2 follow-up). Searches the register
 * (every workspace with a claimed domain), not the caller's own
 * portfolio: a buyer can discover a supplier they've never added and
 * request a disclosure from it directly from the result row.
 */
function SupplierSearch({ slug }: { slug: string }) {
  const [domain, setDomain] = useState('');
  const [queried, setQueried] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<SearchResult | null>(null);

  const runSearch = async () => {
    const trimmed = domain.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const { results: found } = await searchRegister(slug, trimmed);
      setResults(found);
      setQueried(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
        <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-sm)', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
          Search the register
        </h2>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder="Supplier domain or name"
            style={{
              flex: 1, padding: 'var(--space-2)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-card)', background: 'var(--surface)',
              color: 'var(--text)', fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)',
            }}
          />
          <Button onClick={runSearch} disabled={loading || !domain.trim()}>
            {loading ? 'Searching…' : 'Search'}
          </Button>
        </div>
        {error && <Alert variant="error">{error}</Alert>}
        {results && (
          results.length > 0 ? (
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {results.map((r) => (
                <div
                  key={`${r.supplier_display_name}-${r.domain ?? ''}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                    padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-card)',
                  }}
                >
                  <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
                    {r.supplier_display_name}
                    {r.domain && <span style={{ color: 'var(--text-dim)' }}> · {r.domain}</span>}
                  </span>
                  {r.disclosure ? (
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-3xs)', color: 'var(--success-fg)' }}>
                      Disclosed
                    </span>
                  ) : r.request_open ? (
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                      Request sent
                    </span>
                  ) : (
                    <Button onClick={() => setRequesting(r)}>Request disclosure</Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title={`No register match for "${queried}"`} description="No workspace has claimed this domain with a published disclosure yet." />
          )
        )}
      </div>

      {requesting && (
        <RequestDisclosureDialog
          slug={slug}
          domain={requesting.domain ?? domain.trim()}
          onClose={() => setRequesting(null)}
          onSent={() => {
            setRequesting(null);
            setResults((prev) => prev?.map((r) => (r === requesting ? { ...r, request_open: true } : r)) ?? null);
          }}
        />
      )}
    </Card>
  );
}

/** Prompts for the supplier contact email and sends the existing disclosure-request flow. */
function RequestDisclosureDialog({
  slug, domain, onClose, onSent,
}: { slug: string; domain: string; onClose: () => void; onSent: () => void }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = await req<{ rejected?: Array<{ domain: string; reason: string }> }>(
        `/api/w/${encodeURIComponent(slug)}/suppliers/requests`,
        { method: 'POST', body: JSON.stringify({ suppliers: [{ domain, contact_email: email.trim() }] }) }
      );
      if (body.rejected?.length) {
        throw new Error(`Request not sent: ${body.rejected[0].reason}`);
      }
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)', zIndex: 100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card>
        <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)', minWidth: 320 }}>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-md)', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
            Request disclosure from {domain}
          </h2>
          <label style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
            Contact email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              style={{
                display: 'block', width: '100%', marginTop: 'var(--space-1)',
                padding: 'var(--space-2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)', background: 'var(--surface)',
                color: 'var(--text)', fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-2xs)',
              }}
            />
          </label>
          {error && <Alert variant="error">{error}</Alert>}
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !email.trim()}>
              {submitting ? 'Sending…' : 'Send request'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function StatusDot({ hasDisclosure }: { hasDisclosure: boolean }) {
  const color = hasDisclosure ? 'var(--success-fg)' : 'var(--text-dim)';
  const label = hasDisclosure ? 'Disclosure received' : 'No disclosure';
  return (
    <>
      <span
        style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }}
        aria-hidden="true"
      />
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </>
  );
}

function AddSupplierDialog({ slug, onClose }: { slug: string; onClose: () => void }) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await req<unknown>(
        `/api/w/${encodeURIComponent(slug)}/suppliers`,
        { method: 'POST', body: JSON.stringify({ supplier_display_name: name.trim() }) }
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add supplier');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)', zIndex: 100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card>
        <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)', minWidth: 320 }}>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-md)', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
            Add supplier
          </h2>
          <label style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
            Supplier name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              style={{
                display: 'block', width: '100%', marginTop: 'var(--space-1)',
                padding: 'var(--space-2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)', background: 'var(--surface)',
                color: 'var(--text)', fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-2xs)',
              }}
            />
          </label>
          {error && <Alert variant="error">{error}</Alert>}
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !name.trim()}>
              {submitting ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
